import {useEffect, useRef} from 'react';
import { getUtag } from '../ultils';
import { ISubstitute } from '../models/CrossPartDetails';
import { IUtag } from '../models/IUtag';
import { callGTMAnalytics } from '../helper/analyticsTag';
import applierFilterAtom from '../recoils/useApplierFilter/applierFilterAtom';
import { useRecoilState } from 'recoil';
import { TABLE_INTERACT_EVENT } from '../constants';
import isUsingLocalStoredAtom from '../recoils/useIsUsingLocalStored/IsUsingLocalStoredAtom';

interface IProps {
    setIsSearchByUrl: (isSearchByUrl: boolean) => void;
    setUTagData: (utagData: IUtag) => void;
    tableData: ISubstitute[];
    searchQuery: string;
    searchTemp: string;
    isSearchByUrl: boolean;
    isFetching: boolean;
    isNoResult: boolean;
    isError: boolean;
    isNoCrossesFound: boolean;
    isNoProductFound: boolean;
    isMarketplaceApplied: boolean;
    isExcludeTariffApplied: boolean;
    isStockingApplied: boolean;
    isDisplayOptionApplied: boolean;
    haveBasePart: boolean;
    scriptAnalyticIsLoaded:boolean;
  }

const useRefEventForTagging = (props: IProps, refPageEvent: string | undefined, currentUrl?:string, isMultivendor?:boolean, resultNumber?: any) => {
  const [appliedFilters,] = useRecoilState(applierFilterAtom);
  const [isUsingLocalStoraged] = useRecoilState(isUsingLocalStoredAtom);
  const prevUtagRef = useRef<IUtag | null>(null);
  const hasInitialized = useRef(true);
  const prevPartNumber = useRef<string | undefined>(undefined);


  // This will call pageview once when we use short link - search by URL - default search
  useEffect(() => {
    if (
      !props.isFetching &&
      !props.isNoResult &&
      !props.isNoProductFound &&
      !isMultivendor
    ) {
      if(props.isSearchByUrl || isUsingLocalStoraged){
        return;
      }
      const utag = getUtag(
        props.searchTemp,
        props.searchQuery,
        props.tableData,
        props.isDisplayOptionApplied,
        props.isStockingApplied,
        props.isMarketplaceApplied,
        props.isExcludeTariffApplied,
        refPageEvent,
        props.isNoCrossesFound,
        props.isNoProductFound,
        props.isFetching,
        resultNumber
      ) as IUtag
      //save part number to compare when users search new part
      if(prevPartNumber.current === undefined){
        prevPartNumber.current = utag.part_search_term;
      }
      switch (utag.ref_page_event){
        case undefined:
          //Check if it has any checkboxes, then set utag for the second data for search by url.
          if(props.isDisplayOptionApplied || props.isStockingApplied || props.isExcludeTariffApplied || props.isMarketplaceApplied){
            callGTMAnalytics(utag, "Search by Url", appliedFilters.join(','));
            props.setUTagData(utag);
          } else {
              if(hasInitialized.current){
                  hasInitialized.current = false;
                  callGTMAnalytics(utag, "Search by Url", appliedFilters.join(','));
                  props.setUTagData(utag)
              } else {
                hasInitialized.current = true;
              }
          }
          break;
        case "See Example":
          callGTMAnalytics(utag, "View item list", appliedFilters.join(','));
          props.setUTagData(utag);
          break;
    } 
  }
  }, [props.isSearchByUrl, isUsingLocalStoraged, props.isFetching]);

  // This will call pageview once when we use search for part
  useEffect(() => {
    if (
      !props.isFetching &&
      !props.isNoResult &&
      !props.isNoProductFound &&
      !isMultivendor
    ) {
      const utag = getUtag(
        props.searchTemp,
        props.searchQuery,
        props.tableData,
        props.isDisplayOptionApplied,
        props.isStockingApplied,
        props.isMarketplaceApplied,
        props.isExcludeTariffApplied,
        refPageEvent,
        props.isNoCrossesFound,
        props.isNoProductFound,
        props.isFetching,
        resultNumber
      ) as IUtag
      switch (utag.ref_page_event){
        case "Search for Part":
          callGTMAnalytics(utag, "Search for Part", appliedFilters.join(','));
          props.setUTagData(utag);
          break;
      }    
    }  
  }, [resultNumber]);

  // This will call pageview once when we interact with the table - sort, filter, change pagination
  useEffect(()=>{
    if(currentUrl){
      if(TABLE_INTERACT_EVENT.includes(refPageEvent as string)){
        const utag = getUtag(
          props.searchTemp,
          props.searchQuery,
          props.tableData,
          props.isDisplayOptionApplied,
          props.isStockingApplied,
          props.isMarketplaceApplied,
          props.isExcludeTariffApplied,
          refPageEvent,
          props.isNoCrossesFound,
          props.isNoProductFound,
          props.isFetching,
          resultNumber
        ) as IUtag
        props.setUTagData(utag);
        callGTMAnalytics(utag, refPageEvent, appliedFilters.join(','));
      }
    }
  },[currentUrl])

  // This will call pageview once with each action - checkbox include
  useEffect(()=>{
    if (
      (!props.isFetching) ||
      (props.haveBasePart && !props.isFetching)
    ) {
      const utag = getUtag(
          props.searchTemp,
          props.searchQuery,
          props.tableData,
          props.isDisplayOptionApplied,
          props.isStockingApplied,
          props.isMarketplaceApplied,
          props.isExcludeTariffApplied,
          refPageEvent,
          props.isNoCrossesFound,
          props.isNoProductFound,
          props.isFetching,
          resultNumber
        ) as IUtag
        //check utag --> if utag = preUtag then not setUtagData
      if(utag.ref_page_event && utag.ref_page_event === 'Result Options' && JSON.stringify(prevUtagRef.current) !== JSON.stringify(utag)){
        prevUtagRef.current = {...utag};
        props.setUTagData(utag);
        callGTMAnalytics(utag, null, appliedFilters.join(','));
      }
    }
  },[props.isDisplayOptionApplied, props.isStockingApplied, props.isMarketplaceApplied, props.isExcludeTariffApplied])

    // This will call pageview once when no result/no cross overlay appeared
    useEffect(() => {
        if (props.scriptAnalyticIsLoaded) {
            //first default load - not search by url or use shared link
            if (!window.location.href.includes('/cross-reference?search=') && !window.location.href.includes('/cross-reference/')
                && !window.location.href.includes('/cross-reference?s=')) {
                props.setIsSearchByUrl(false);
            }

        }
        if (props.isNoResult && refPageEvent !== "Reverse Lookup") {
            const utag = getUtag(
                props.searchTemp,
                props.searchQuery,
                [],
                props.isDisplayOptionApplied,
                props.isStockingApplied,
                props.isMarketplaceApplied,
                props.isExcludeTariffApplied,
                refPageEvent,
                props.isNoCrossesFound,
                props.isNoProductFound,
                props.isFetching,
                resultNumber
            ) as IUtag
            props.setUTagData(utag);
            if (!utag.ref_page_event) {
                callGTMAnalytics(utag, "Search by Url", appliedFilters.join(','));
            }
            if (utag.ref_page_event === "Search for Part") {
                callGTMAnalytics(utag, null, appliedFilters.join(','));
            }
            if (props.isSearchByUrl) {
                props.setIsSearchByUrl(false);
            }
        }
    }, [props.isNoResult, props.scriptAnalyticIsLoaded]);


  // This will call pageview once when error occurs
  useEffect(() => {
    if (props.isError) {
      const utag = getUtag(
        props.searchTemp,
        props.searchQuery,
        [],
        props.isDisplayOptionApplied,
        props.isStockingApplied,
        props.isMarketplaceApplied,
        props.isExcludeTariffApplied,
        refPageEvent,
        props.isNoCrossesFound,
        props.isNoProductFound,
        props.isFetching,
        resultNumber
      ) as IUtag
      props.setUTagData(utag);
      callGTMAnalytics(utag, "Error", null);
    }
  }, [props.isError]);
}

export default useRefEventForTagging